import React, { useState, useCallback } from "react";
import {
  Accordion, AccordionSummary, AccordionDetails, CircularProgress,
  Box, Grid, Button, Typography, Stack, Chip, Table, TableHead,
  TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon, PictureAsPdf as PdfIcon, Info as InfoIcon } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { fetchReports } from "../../../ducks/reportsDuck";
import { useTranslation } from "react-i18next";
import { useSystemId, useReports } from "../../../hooks/useReduxSelectors";
import { getReportUrl } from "../../../constants/apiUrl";
import InfoModal from "./InfoModal";
import ReportSearchBar from "../reportSearchBar/ReportSearchBar";
import ManualRunControls from "../manualRunControls/ManualRunControls";
import QAL3PrintVersion from "./QAL3PrintVersion";
import { formatDateTime } from "../../../helpers/utils";

const driftCheckColor = (status) => {
  if (status === 1) return "primary.main";
  if (status === 2) return "secondary.main";
  if (status === -1) return "error.main";
  return "text.secondary";
};

const driftCheckLabel = (status) => {
  if (status === 1) return "Checked";
  if (status === 2) return "Calibrate";
  if (status === -1) return "Failure";
  return "N/A";
};

const responseCheckColor = (status) => (status === 1 ? "primary.main" : "error.main");

const QAL3Report = ({ systemName }) => {
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [infoIndex, setInfoIndex] = useState(0);

  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const systemId = useSystemId();
  const reports = useReports();
  const values = reports?.QAL3?.entries;

  const fetchQAL3Report = useCallback(() => {
    const args = { ReportName: "QAL3", Count: 1 };
    dispatch(
      fetchReports({ systemId, url: getReportUrl, type: "QAL3", args, t })
    ).then(() => setLoading(false));
  }, [dispatch, t]);

  const onInfoClicked = (idx) => {
    setInfoIndex(idx);
    setIsModalOpen(true);
  };

  const formatDuration = (seconds) =>
    Math.floor(seconds / 60) + " min " + Math.floor(seconds % 60) + "s";

  const formatEnergyKey = (key) => {
    if (key.length === 8) return key.slice(0, 3) + " " + key.slice(3, 5) + " " + key.slice(5, 8);
    if (key.length === 9) return key.slice(0, 3) + " " + key.slice(3, 6) + " " + key.slice(6, 9);
    return key;
  };

  const headerCellSx = {
    fontWeight: 600,
    fontSize: "0.75rem",
    color: "text.secondary",
    py: 0.5,
    px: 1,
    whiteSpace: "nowrap",
    borderBottom: "2px solid",
    borderColor: "divider",
  };

  const cellSx = {
    py: 0.5,
    px: 1,
    fontSize: "0.8rem",
    borderColor: "divider",
  };

  return (
    <>
      <QAL3PrintVersion
        isOpen={isPrintDialogOpen}
        onClose={() => setIsPrintDialogOpen(false)}
        values={values}
        systemName={systemName}
        loading={loading}
        t={t}
      />
      <Accordion
        disableGutters
        onChange={(e, expanded) => { if (expanded) fetchQAL3Report(); }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "common.white" }} />}
          sx={{ bgcolor: "primary.main", color: "common.white" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 1 }}>
            <Typography sx={{ fontWeight: 600 }}>QAL 3</Typography>
            <ManualRunControls reportName="QAL3" />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 1.5 }}>
          <ReportSearchBar reportName="QAL3" openPrintDialog={() => setIsPrintDialogOpen(true)} />

          {/* <Stack direction="row" spacing={1} sx={{ px: 1.5, pb: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PdfIcon />}
              onClick={() => setIsPrintDialogOpen(true)}
            >
              {t("Download PDF")}
            </Button>
          </Stack> */}

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!loading && (
            <InfoModal
              values={values}
              show={isModalOpen}
              onHide={() => setIsModalOpen(false)}
              infoIndex={infoIndex}
              t={t}
            />
          )}

          <Stack spacing={1.5} sx={{ px: 0.5 }}>
            {!loading && values?.length > 0 && values.map((val, idx) => (
              <Accordion
                key={`qal3-${idx}`}
                defaultExpanded={idx === 0}
                disableGutters
                sx={{ overflow: "hidden" }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "common.white" }} />}
                  sx={{
                    bgcolor: val.State === "QAL3 Failed" ? "error.main" : "primary.main",
                    color: "common.white",
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>
                    {formatDateTime(val.ReportTimeStamp)} : {val.State}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1.5, bgcolor: "background.mid" }}>
                  {/* Header info */}
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ mb: 2 }}>
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {formatDateTime(val.ReportTimeStamp)}
                        </Typography>
                        <Chip
                          label={val.State}
                          size="small"
                          sx={{
                            bgcolor: val.State === "QAL3 Failed" ? "error.main" : "primary.main",
                            color: "common.white",
                            fontWeight: 600,
                          }}
                        />
                      </Stack>
                      <Stack direction="row" spacing={3} sx={{ color: "text.secondary" }}>
                        <Typography variant="body2">{val.RunType}</Typography>
                        <Typography variant="body2">{t("Duration")}: {formatDuration(val.Duration)}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={3} sx={{ color: "text.secondary" }}>
                        <Typography variant="body2">{t("Drift tests")}: {val.DriftTests}</Typography>
                        <Typography variant="body2">{t("Calibrations")}: {val.Calibrations}</Typography>
                        <Typography variant="body2">{t("Response times")}: {val.ResponseTimes}</Typography>
                      </Stack>
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<InfoIcon />}
                      onClick={() => onInfoClicked(idx)}
                    >
                      Info
                    </Button>
                  </Stack>

                  {/* Gas analysis table */}
                  <TableContainer sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headerCellSx}>{t("Gas")}</TableCell>
                          <TableCell sx={headerCellSx}>{t("Zero Drift")}</TableCell>
                          <TableCell sx={headerCellSx}>{t("Span Drift")}</TableCell>
                          <TableCell sx={headerCellSx}>{t("Zero Response")}</TableCell>
                          <TableCell sx={headerCellSx}>{t("Span Response")}</TableCell>
                          <TableCell sx={headerCellSx}>{t("Zero Result")}</TableCell>
                          <TableCell sx={headerCellSx}>{t("Span Result")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(val.ComponentResults).map(([gas, v]) => (
                          <TableRow key={gas} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                            <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{gas}</TableCell>
                            <TableCell sx={cellSx}>
                              <Box>
                                <Typography variant="body2" sx={{ color: driftCheckColor(v.ZeroDriftStatus) }}>
                                  {Number(v.ZeroDrift).toFixed(1)} {v.Unit}
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ color: "text.disabled" }}>
                                  {v.ZeroCalLimit} / {v.ZeroFailLimit} {v.Unit}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={cellSx}>
                              <Box>
                                <Typography variant="body2" sx={{ color: driftCheckColor(v.SpanDriftStatus) }}>
                                  {Number(v.SpanDrift).toFixed(1)} %
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ color: "text.disabled" }}>
                                  {v.SpanCalLimit}% / {v.SpanFailLimit}%
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={cellSx}>
                              <Box>
                                <Typography variant="body2" sx={{ color: responseCheckColor(v.ZeroResponseStatus) }}>
                                  {Math.round(v.ZeroResponseTime)} s
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ color: "text.disabled" }}>
                                  {v.ZeroResponseTimeLimit} s
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={cellSx}>
                              <Box>
                                <Typography variant="body2" sx={{ color: responseCheckColor(v.SpanResponseStatus) }}>
                                  {Math.round(v.SpanResponseTime)} s
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ color: "text.disabled" }}>
                                  {v.SpanResponseTimeLimit} s
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={cellSx}>
                              <Chip
                                label={driftCheckLabel(v.ZeroDriftStatus)}
                                size="small"
                                sx={{ bgcolor: driftCheckColor(v.ZeroDriftStatus), color: "common.white", fontWeight: 600, fontSize: "0.7rem" }}
                              />
                            </TableCell>
                            <TableCell sx={cellSx}>
                              <Chip
                                label={driftCheckLabel(v.SpanDriftStatus)}
                                size="small"
                                sx={{ bgcolor: driftCheckColor(v.SpanDriftStatus), color: "common.white", fontWeight: 600, fontSize: "0.7rem" }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Energy levels */}
                  {val.EnergyLevels && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        {t("Energy Levels")}
                      </Typography>
                      <Grid container spacing={0.5}>
                        {Object.entries(val.EnergyLevels).map(([key, value]) => (
                          <Grid size={{ xs: 6, sm: 4, md: 3 }} key={key}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", bgcolor: "background.dark", borderRadius: 0.5, px: 1, py: 0.5 }}>
                              <Typography variant="caption">{formatEnergyKey(key)}</Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>{value.toFixed(2)} %</Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </>
  );
};
export default QAL3Report;
